/** Answers as people read them: shared by the ad page (web) and the review page (admin). */
import { getForm, steps, type Field } from "./index";

const inr = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

/** ₹12,50,000 (Indian digit grouping). */
export const rupees = (n: number | null | undefined) => (n == null ? "—" : `₹${inr.format(n)}`);

/** Short prices for cards: ₹18,000 · ₹45 L · ₹1.25 Cr. */
export function shortRupees(n: number | null | undefined): string {
  if (n == null) return "Price on request";
  const trim = (x: number) => String(Number(x.toFixed(2)));
  if (n >= 1e7) return `₹${trim(n / 1e7)} Cr`;
  if (n >= 1e5) return `₹${trim(n / 1e5)} L`;
  return `₹${inr.format(n)}`;
}

/** An answer in words. Inside an object, a place id (locality_id ...) shows as its name and an address as
 * itself, so a service's "Locality / studio address" reads "Ameerpet · 12 Main Road", not "locality id: L022222". */
export function plain(value: unknown, places: Record<string, string> = {}): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map((v) => plain(v, places)).join(", ");
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined && v !== null && v !== "")
      .map(([k, v]) => {
        if (/_id$/.test(k) && typeof v === "string") return places[v] ?? v;
        if (k === "address") return plain(v, places);
        return typeof v === "object" && v !== null && !Array.isArray(v) ? `${k}: ${plain(v, places)}` : `${k.replace(/_/g, " ")}: ${plain(v, places)}`;
      })
      .join(" · ");
  }
  return String(value);
}

export interface Room {
  name: string;
  length: number;
  width: number;
}

/** A floor plan's room sizes: {unit, rooms}; anything else gives no rooms. */
export function roomsOf(value: unknown): { unit: "ft" | "m"; rooms: Room[] } {
  const v = (value && typeof value === "object" ? value : {}) as { unit?: string; rooms?: unknown };
  const rooms = Array.isArray(v.rooms)
    ? (v.rooms as Partial<Room>[])
        .filter((r) => r && r.name && Number(r.length) > 0 && Number(r.width) > 0)
        .map((r) => ({ name: String(r.name), length: Number(r.length), width: Number(r.width) }))
    : [];
  return { unit: v.unit === "m" ? "m" : "ft", rooms };
}

/** One room's size in feet or metres: "10 × 14 ft" / "3.05 × 4.27 m". */
export function roomSize(r: Room, from: "ft" | "m", to: "ft" | "m"): string {
  const k = from === to ? 1 : from === "ft" ? 0.3048 : 1 / 0.3048;
  const n = (x: number) => String(Number((x * k).toFixed(to === "m" ? 2 : 1)));
  return `${n(r.length)} × ${n(r.width)} ${to}`;
}

function roomsText(value: unknown): string {
  const { unit, rooms } = roomsOf(value);
  return rooms.length ? rooms.map((r) => `${r.name} ${roomSize(r, unit, unit)}`).join(", ") : "—";
}

/** One answer as staff read it. `places` turns location ids into names. */
export function formatAnswer(field: Field | undefined, value: unknown, places: Record<string, string> = {}): string {
  if (!field) return plain(value);
  switch (field.type) {
    case "money":
      return typeof value === "number" || (typeof value === "string" && value !== "") ? rupees(Number(value)) : "—";
    case "measure": {
      const m = value as { value?: unknown; unit?: string } | null;
      return m?.value != null ? `${inr.format(Number(m.value))}${m.unit ? ` ${m.unit}` : ""}` : "—";
    }
    case "location":
      return typeof value === "string" ? (places[value] ?? value) : "—";
    case "geo": {
      const g = value as { lat?: number; lng?: number } | null;
      return g?.lat != null ? `${g.lat}, ${g.lng}` : "—";
    }
    case "media":
      return Array.isArray(value) ? `${value.length} file${value.length === 1 ? "" : "s"}` : "—";
    case "object":
      return /^room sizes table/i.test(field.control) ? roomsText(value) : plain(value, places);
    default:
      return plain(value, places);
  }
}

export interface AnswerRow {
  label: string;
  value: string;
}

export interface AnswerSection {
  title: string;
  rows: AnswerRow[];
}

/** The ad's answers in the same steps the owner filled them in; empty answers are left out. */
export function answerSections(formKey: string, answers: Record<string, unknown>, places: Record<string, string> = {}): AnswerSection[] {
  const form = getForm(formKey);
  if (!form) return [{ title: "Answers", rows: Object.entries(answers).map(([k, v]) => ({ label: k, value: plain(v) })) }];
  const out: AnswerSection[] = [];
  const shown = new Set<string>();
  for (const step of steps(form)) {
    if (step.group) {
      const items = Array.isArray(answers[step.group]) ? (answers[step.group] as Record<string, unknown>[]) : [];
      shown.add(step.group);
      items.forEach((item, i) =>
        out.push({
          title: `${step.title} ${i + 1}`,
          rows: step.fields.filter((f) => item[f.name] !== undefined).map((f) => ({ label: f.label, value: formatAnswer(f, item[f.name], places) })),
        }),
      );
      continue;
    }
    const rows = step.fields
      .filter((f) => answers[f.name] !== undefined && answers[f.name] !== "")
      .map((f) => {
        shown.add(f.name);
        return { label: f.label, value: formatAnswer(f, answers[f.name], places) };
      });
    if (rows.length) out.push({ title: step.title, rows });
  }
  const other = Object.keys(answers).filter((k) => !shown.has(k));
  if (other.length) out.push({ title: "Other answers", rows: other.map((k) => ({ label: k, value: plain(answers[k]) })) });
  return out;
}
